import { useQuery } from '@apollo/client/react';
import {
  Box,
  Button,
  Heading,
  HStack,
  IconButton,
  Link,
  Table,
  Text,
  VStack,
} from '@chakra-ui/react';
import React from 'react';
import { FiInfo } from 'react-icons/fi';
import { Link as RouterLink } from 'react-router-dom';

import { PageContentRoot } from '#components/page-content-root/page-content-root';
import { Tooltip } from '#components/tooltip/tooltip';
import {
  GET_AI_AUDIT_FILTER_OPTIONS,
  GET_AI_AUDITS,
} from '#graphql/queries/ai-audit';
import { instanceIdLinkLabel } from '#utils/instance-id-link-label';

/**
 * Returns metadata fields for list tooltip: stored blob minus fields already in other table columns
 * (token counts, legacy executionId alias).
 */
function metadataRestForTooltip(
  metadata: unknown,
): Record<string, unknown> | null {
  if (!metadata || typeof metadata !== 'object' || Array.isArray(metadata)) {
    return null;
  }

  const m = { ...(metadata as Record<string, unknown>) };
  delete m.executionId;
  delete m.tokenIn;
  delete m.tokenOut;
  return Object.keys(m).length > 0 ? m : null;
}

export function AiAuditListPage() {
  const [endpointFilter, setEndpointFilter] = React.useState('');
  const [processorIdFilter, setProcessorIdFilter] = React.useState('');

  const { data: optionsData } = useQuery(GET_AI_AUDIT_FILTER_OPTIONS);
  const { data, loading, error, refetch } = useQuery(GET_AI_AUDITS, {
    variables: {
      limit: 200,
      endpoint: endpointFilter || null,
      processorId: processorIdFilter || null,
    },
  });

  const rows = data?.aiAudits ?? [];
  const endpoints = optionsData?.aiAuditFilterOptions.endpoints ?? [];
  const processorIds = optionsData?.aiAuditFilterOptions.processorIds ?? [];

  if (loading && !data) {
    return (
      <PageContentRoot>
        <Heading size="lg" color="fg" mb={6}>
          AI audit log
        </Heading>
        <Text>Loading…</Text>
      </PageContentRoot>
    );
  }

  if (error) {
    return (
      <PageContentRoot>
        <Heading size="lg" color="fg" mb={6}>
          AI audit log
        </Heading>
        <Text color="red.500">Error loading audit log: {error.message}</Text>
        <Button onClick={() => refetch()} mt={4}>
          Retry
        </Button>
      </PageContentRoot>
    );
  }

  return (
    <PageContentRoot>
      <VStack gap={4} align="stretch">
        <Heading size="lg" color="fg">
          AI audit log
        </Heading>
        <HStack flexWrap="wrap" gap={4} align="flex-end">
          <Box maxW="280px">
            <Text fontSize="sm" color="fg.muted" mb={1}>
              Endpoint
            </Text>
            <select
              value={endpointFilter}
              onChange={(e) => setEndpointFilter(e.target.value)}
              style={{
                width: '100%',
                padding: '6px 8px',
                borderRadius: '6px',
                borderWidth: '1px',
              }}
            >
              <option value="">All</option>
              {endpoints.map((ep) => (
                <option key={ep} value={ep}>
                  {ep}
                </option>
              ))}
            </select>
          </Box>
          <Box maxW="280px">
            <Text fontSize="sm" color="fg.muted" mb={1}>
              Processor id
            </Text>
            <select
              value={processorIdFilter}
              onChange={(e) => setProcessorIdFilter(e.target.value)}
              style={{
                width: '100%',
                padding: '6px 8px',
                borderRadius: '6px',
                borderWidth: '1px',
              }}
            >
              <option value="">All</option>
              {processorIds.map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </select>
          </Box>
          <Button size="sm" onClick={() => refetch()}>
            Refresh
          </Button>
        </HStack>
        {rows.length === 0 ? (
          <Text color="fg.muted">No audit rows match the filters.</Text>
        ) : (
          <Box overflowX="auto">
            <Table.Root
              size="sm"
              variant="line"
              css={{
                '& thead tr, & tbody tr': {
                  backgroundColor: 'var(--input-base-background-color)',
                },
              }}
            >
              <Table.Header>
                <Table.Row>
                  <Table.ColumnHeader>Created</Table.ColumnHeader>
                  <Table.ColumnHeader>Endpoint</Table.ColumnHeader>
                  <Table.ColumnHeader textAlign="end">
                    Token in
                  </Table.ColumnHeader>
                  <Table.ColumnHeader textAlign="end">
                    Token out
                  </Table.ColumnHeader>
                  <Table.ColumnHeader>Metadata</Table.ColumnHeader>
                  <Table.ColumnHeader textAlign="end">
                    Instance
                  </Table.ColumnHeader>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {rows.map((row) => {
                  const metadataRest = metadataRestForTooltip(row.metadata);
                  return (
                    <Table.Row key={row.id}>
                      <Table.Cell whiteSpace="nowrap">
                        {new Date(row.createdAt).toLocaleString()}
                      </Table.Cell>
                      <Table.Cell fontFamily="mono" fontSize="xs">
                        {row.endpoint}
                      </Table.Cell>
                      <Table.Cell
                        textAlign="end"
                        fontFamily="mono"
                        fontSize="xs"
                      >
                        {row.tokenIn != null ? row.tokenIn : '—'}
                      </Table.Cell>
                      <Table.Cell
                        textAlign="end"
                        fontFamily="mono"
                        fontSize="xs"
                      >
                        {row.tokenOut != null ? row.tokenOut : '—'}
                      </Table.Cell>
                      <Table.Cell>
                        {metadataRest ? (
                          <Tooltip
                            content={
                              <Box
                                as="pre"
                                fontFamily="mono"
                                fontSize="xs"
                                whiteSpace="pre-wrap"
                                m={0}
                                textAlign="left"
                              >
                                {JSON.stringify(metadataRest, null, 2)}
                              </Box>
                            }
                            contentProps={{
                              maxW: 'sm',
                              maxH: '200px',
                              overflow: 'auto',
                            }}
                            showArrow
                          >
                            <IconButton
                              type="button"
                              variant="ghost"
                              size="xs"
                              aria-label="View request metadata"
                            >
                              <FiInfo size={16} aria-hidden />
                            </IconButton>
                          </Tooltip>
                        ) : (
                          <Text color="fg.muted" fontSize="sm">
                            —
                          </Text>
                        )}
                      </Table.Cell>
                      <Table.Cell
                        textAlign="end"
                        fontFamily="mono"
                        fontSize="xs"
                      >
                        {row.instanceId ? (
                          <Text fontSize="sm">
                            {instanceIdLinkLabel(row.instanceId)}&nbsp;
                            <Link
                              asChild
                              color="blue.600"
                              title={row.instanceId}
                            >
                              <RouterLink
                                to={`/instance/${encodeURIComponent(row.instanceId)}`}
                              >
                                View
                              </RouterLink>
                            </Link>
                          </Text>
                        ) : (
                          <Text color="fg.muted" fontSize="sm">
                            —
                          </Text>
                        )}
                      </Table.Cell>
                    </Table.Row>
                  );
                })}
              </Table.Body>
            </Table.Root>
          </Box>
        )}
      </VStack>
    </PageContentRoot>
  );
}
